// Credit status derives from the balance, never from the public plan allowance.
// A saved subscription can have a different grant from today's catalog.
//
// One more fact outranks a comfortable forecast: the last AI request the server
// REFUSED for credits (`root._creditsShort`, held by functions/creditGuard.js
// notifyCreditsExhaustedFromMessage — "needed 222, available 100"). While the
// balance read here is still below what that request needed, the page says so —
// it must never print "Healthy balance" / "No top-up needed now" under a thread
// that has just said the workspace cannot afford the person's request (persona
// audit round 1: all four new accounts saw both at once).
export const AdminUsageSummary = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
  gap: 'A',
  '@tabletS': { gridTemplateColumns: 'minmax(0, 1fr)' },
  '@appMobile': { gridTemplateColumns: 'minmax(0, 1fr)' },
  scope: {
    balance: (root) => root?._usageBalance?.data || root?._usageBalance || {},
    available: (root) => {
      const data = root?._usageBalance?.data || root?._usageBalance
      return !!data && !data.error && Number.isFinite(Number(data.total)) && data.total != null
    },
    usedFraction: (root) => {
      const data = root?._usageBalance?.data || root?._usageBalance || {}
      const size = Number(data.grantSize)
      if (
        !Number.isFinite(size) ||
        !(size > 0) ||
        data.grantRemaining == null ||
        !Number.isFinite(Number(data.grantRemaining))
      )
        return null
      return Math.min(1, Math.max(0, 1 - Number(data.grantRemaining) / size))
    },
    // Forecasts use the server's observed window, never an invented allowance.
    // Organization pools cannot use one workspace's spend as their burn rate.
    health: (root) => {
      const balance = root?._usageBalance?.data || root?._usageBalance || {}
      const usage = root?._usageByActor?.data || root?._usageByActor || {}
      const total = Number(balance.total)
      const available = balance.total != null && Number.isFinite(total) && !balance.error
      const pooled = balance.scope === 'organization'
      const spent =
        !pooled &&
        !root?._usageLoadingAt &&
        !usage.error &&
        usage.totalCredits != null &&
        Number.isFinite(Number(usage.totalCredits))
          ? Math.max(0, Number(usage.totalCredits))
          : null
      const elapsed = (Date.parse(usage.to) - Date.parse(usage.from)) / 86400000
      const daily = spent > 0 && usage.period === 'month' && elapsed >= 1 ? spent / elapsed : null
      const days = available && daily ? Math.max(0, total) / daily : null
      const size = Number(balance.grantSize)
      const hasGrant =
        !pooled &&
        size > 0 &&
        Number.isFinite(size) &&
        balance.grantRemaining != null &&
        Number.isFinite(Number(balance.grantRemaining))
      const ratio = hasGrant ? Math.max(0, total) / size : null
      const resetInDays = (Date.parse(balance.nextResetAt) - Date.parse(usage.to)) / 86400000
      const resetBeforeEmpty = hasGrant && days != null && resetInDays > 0 && resetInDays <= days
      // The refused request's need, while this page's own (fresh) balance is
      // still below it. An EMPTY balance is the next branch's, not this one's.
      const heldNeed = Number(root?._creditsShort?.needed)
      const shortNeeded =
        available && total > 0 && Number.isFinite(heldNeed) && heldNeed > total ? heldNeed : null
      const status = !available
        ? 'unknown'
        : total <= 0
          ? 'empty'
          : shortNeeded != null
            ? 'short'
            : resetBeforeEmpty
              ? 'healthy'
              : days != null
                ? days <= 3
                  ? 'critical'
                  : days <= 7
                    ? 'low'
                    : 'healthy'
                : ratio != null
                  ? ratio <= 0.1
                    ? 'critical'
                    : ratio <= 0.3
                      ? 'low'
                      : 'healthy'
                  : 'available'
      const tone =
        status === 'empty' || status === 'critical'
          ? 'dangerInk'
          : status === 'low' || status === 'short'
            ? 'warningInk'
            : status === 'unknown'
              ? 'inactive'
              : 'successInk'
      const fill =
        status === 'empty' || status === 'critical'
          ? 'red'
          : status === 'low' || status === 'short'
            ? 'yellow'
            : 'green'
      const kind = hasGrant ? 'grant' : spent != null ? 'balance' : 'allocation'
      const remaining = Math.max(0, Number(balance.grantRemaining) || 0)
      const chartLeft =
        kind === 'grant'
          ? Math.min(size, remaining)
          : kind === 'balance'
            ? Math.max(0, total || 0)
            : remaining
      const chartRight =
        kind === 'grant'
          ? Math.max(0, size - remaining)
          : kind === 'balance'
            ? spent
            : Math.max(0, Number(balance.topupRemaining) || 0)
      const fraction = chartLeft + chartRight > 0 ? chartLeft / (chartLeft + chartRight) : 0
      const statusKey = {
        healthy: 'admin.usage.health.healthy',
        low: 'admin.usage.health.low',
        critical: 'admin.usage.health.critical',
        empty: 'admin.usage.health.empty',
        available: 'admin.usage.health.available',
        short: 'admin.usage.health.short',
        unknown: 'admin.usage.summary.loading'
      }[status]
      const guideKey = {
        healthy: 'admin.usage.guide.healthy',
        low: 'admin.usage.guide.low',
        critical: 'admin.usage.guide.critical',
        empty: 'admin.usage.guide.empty',
        available: 'admin.usage.guide.available',
        short: 'admin.usage.guide.short',
        unknown: 'admin.usage.summary.loading'
      }[status]
      const graphKey = {
        grant: 'admin.usage.graph.grant',
        balance: 'admin.usage.graph.balance',
        allocation: 'admin.usage.graph.allocation'
      }[kind]
      return {
        statusKey,
        guideKey,
        graphKey,
        shortNeeded,
        available,
        total,
        spent,
        daily,
        days,
        resetBeforeEmpty,
        status,
        tone,
        fill,
        kind,
        chartLeft,
        chartRight,
        fraction,
        refillAt: resetBeforeEmpty
          ? null
          : daily
            ? Math.ceil(daily * 7)
            : hasGrant
              ? Math.ceil(size * 0.3)
              : null
      }
    },
    formatCredits: (value) =>
      value == null ? '—' : Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 }),
    plan: (root) => {
      const balance = root?._usageBalance?.data || root?._usageBalance || {}
      const rates = root?._usageRates?.data || root?._usageRates || {}
      return Object.values(rates.tiers || {}).find((tier) => tier.key === balance.tier)
    }
  },
  AdminCard: {
    padding: 'A1',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    alignContent: 'start',
    alignItems: 'center',
    childProps: { gridColumn: '1 / -1' },
    gap: 'A',
    minWidth: '0',
    Header: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z',
      flexWrap: 'wrap',
      Label: {
        text: (el) => el.call('polyglot', 'admin.usage.balance.total'),
        color: 'inactive',
        fontSize: 'Z'
      },
      Status: {
        role: 'status',
        round: 'radiusPill',
        padding: 'Y Z',
        background: 'selected',
        fontSize: 'Y2',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => {
          const health = el.scope.health(s.root)
          if (!health.available)
            return el.call(
              'polyglot',
              el.scope.balance(s.root).error
                ? 'admin.usage.error.balance'
                : 'admin.usage.summary.loading'
            )
          const text = el.call('polyglot', health.statusKey)
          return health.shortNeeded == null
            ? text
            : text
                .replace('{needed}', () => el.scope.formatCredits(health.shortNeeded))
                .replace('{available}', () => el.scope.formatCredits(health.total))
        }
      }
    },
    Total: {
      gridColumn: '1',
      gridRow: '2',
      minWidth: '0',
      overflowWrap: 'anywhere',
      fontSize: 'F',
      '@mobileL': { fontSize: 'E' },
      '@appMobile': { fontSize: 'E' },
      fontWeight: '700',
      lineHeight: '1',
      color: 'title',
      fontVariantNumeric: 'tabular-nums',
      text: (el, s) =>
        el.scope.available(s.root) ? el.scope.formatCredits(el.scope.balance(s.root).total) : '—'
    },
    Gauge: {
      hide: (el, s) => !el.scope.available(s.root),
      gridColumn: '2',
      gridRow: '2',
      boxSize: 'F',
      padding: 'Z',
      '@mobileL': { boxSize: 'D', padding: 'Y' },
      '@appMobile': { boxSize: 'D', padding: 'Y' },
      boxSizing: 'border-box',
      round: 'radiusPill',
      ariaHidden: 'true',
      background: (el, s) => {
        const h = el.scope.health(s.root)
        return `conic-gradient(var(--color-${h.fill}) ${h.fraction * 360}deg, var(--color-widgetPlate) 0deg)`
      },
      Center: {
        width: '100%',
        height: '100%',
        round: 'radiusPill',
        background: 'cardSolid',
        align: 'center center',
        fontSize: 'C',
        '@mobileL': { fontSize: 'Z2' },
        '@appMobile': { fontSize: 'Z2' },
        fontWeight: '700',
        color: 'title',
        fontVariantNumeric: 'tabular-nums',
        text: (el, s) => `${Math.round(el.scope.health(s.root).fraction * 100)}%`
      }
    },
    LimitBar: {
      hide: (el, s) => !el.scope.available(s.root),
      flow: 'y',
      gap: 'Z',
      padding: 'A',
      background: 'widgetPlate',
      round: 'radiusControl',
      LabelRow: {
        flow: 'x',
        align: 'center space-between',
        flexWrap: 'wrap',
        gap: 'Y',
        Label: {
          text: (el, s) => el.call('polyglot', el.scope.health(s.root).graphKey),
          fontSize: 'Z',
          color: 'title',
          fontWeight: '500'
        },
        Value: {
          fontSize: 'Z',
          fontWeight: '700',
          color: (el, s) => el.scope.health(s.root).tone,
          text: (el, s) => {
            const health = el.scope.health(s.root)
            return health.kind === 'grant'
              ? el
                  .call('polyglot', 'admin.usage.graph.percentLeft')
                  .replace('{percent}', Math.round(health.fraction * 100))
              : el
                  .call('polyglot', 'admin.usage.graph.available')
                  .replace('{credits}', el.scope.formatCredits(health.total))
          }
        }
      },
      ProgressBar: {
        height: 'A',
        background: 'cardSolid',
        'aria-label': (el, s) => el.call('polyglot', el.scope.health(s.root).graphKey),
        'aria-valuetext': (el, s) =>
          el
            .call('polyglot', 'admin.usage.graph.available')
            .replace('{credits}', el.scope.formatCredits(el.scope.health(s.root).total)),
        value: (el, s) => el.scope.health(s.root).fraction,
        'aria-valuenow': (el, s) => String(Math.round(el.scope.health(s.root).fraction * 100)),
        Fill: {
          transition: 'none',
          width: (el, s) => `${el.scope.health(s.root).fraction * 100}%`,
          background: (el, s) => el.scope.health(s.root).fill
        }
      },
      Scale: {
        // The axis clarifies the meter without inventing a usage history.
        flow: 'x',
        align: 'start space-between',
        ariaHidden: 'true',
        childExtends: 'Text',
        childrenAs: 'state',
        children: [{ value: 0 }, { value: 25 }, { value: 50 }, { value: 75 }, { value: 100 }],
        childProps: {
          flow: 'y',
          gap: 'Y',
          color: 'caption',
          fontSize: 'Y',
          fontVariantNumeric: 'tabular-nums',
          text: (el, s) => `${s.value}%`,
          ':before': {
            content: '""',
            width: 'hairline',
            height: 'Y',
            background: 'inactive'
          },
          ':last-child': { alignItems: 'end' }
        }
      },
      Legend: {
        tag: 'div',
        flow: 'x',
        align: 'center space-between',
        flexWrap: 'wrap',
        gap: 'Z',
        childExtends: 'AdminUsageLegend',
        childrenAs: 'state',
        children: (el, s) => {
          const health = el.scope.health(s.root)
          return [
            {
              color: health.fill,
              label:
                health.kind === 'allocation'
                  ? 'admin.usage.balance.fromGrant'
                  : 'admin.usage.graph.left',
              value: el.scope.formatCredits(health.chartLeft)
            },
            {
              color: 'inactive',
              label:
                health.kind === 'allocation'
                  ? 'admin.usage.balance.fromTopups'
                  : health.kind === 'grant'
                    ? 'admin.usage.graph.used'
                    : 'admin.usage.graph.spent',
              value: el.scope.formatCredits(health.chartRight)
            }
          ]
        }
      },
      Remaining: {
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => {
          const balance = el.scope.balance(s.root)
          const health = el.scope.health(s.root)
          return health.kind === 'grant'
            ? el
                .call('polyglot', 'admin.usage.amountOf')
                .replace('{remaining}', el.scope.formatCredits(balance.grantRemaining))
                .replace('{total}', el.scope.formatCredits(balance.grantSize))
            : el.call(
                'polyglot',
                health.kind === 'balance'
                  ? 'admin.usage.graph.comparisonHint'
                  : 'admin.usage.graph.allocationHint'
              )
        }
      }
    },
    Outlook: {
      hide: (el, s) => !el.scope.available(s.root),
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: 'A',
      '@mobileL': { gridTemplateColumns: 'minmax(0, 1fr)' },
      '@appMobile': { gridTemplateColumns: 'minmax(0, 1fr)' },
      childExtends: 'AdminUsageMetric',
      childProps: {
        paddingLeft: 'Z',
        borderLeft: 'hairline solid line'
      },
      childrenAs: 'state',
      children: (el, s) => {
        const health = el.scope.health(s.root)
        const days = health.days
        return [
          {
            label: 'admin.usage.guide.cover',
            value:
              days == null
                ? '—'
                : el
                    .call(
                      'polyglot',
                      days >= 30
                        ? 'admin.usage.guide.days30'
                        : days < 1
                          ? 'admin.usage.guide.lessDay'
                          : 'admin.usage.guide.days'
                    )
                    .replace('{days}', Math.floor(days))
          },
          {
            label: 'admin.usage.guide.daily',
            value: health.daily == null ? '—' : el.scope.formatCredits(Math.round(health.daily))
          },
          {
            label: 'admin.usage.guide.refillAt',
            value: health.refillAt == null ? '—' : el.scope.formatCredits(health.refillAt)
          }
        ]
      }
    },
    Guidance: {
      hide: (el, s) => !el.scope.available(s.root),
      background: 'selected',
      round: 'radiusControl',
      padding: 'A',
      flow: 'x',
      align: 'center space-between',
      flexWrap: 'wrap',
      gap: 'A',
      Copy: {
        flex: '1',
        minWidth: '0',
        flow: 'y',
        gap: 'Y',
        Title: {
          fontSize: 'Z2',
          fontWeight: '700',
          color: 'title',
          text: (el, s) => el.call('polyglot', el.scope.health(s.root).guideKey)
        },
        Detail: {
          fontSize: 'Y2',
          color: 'caption',
          text: (el, s) => {
            const health = el.scope.health(s.root)
            const text = el.call(
              'polyglot',
              health.shortNeeded != null
                ? 'admin.usage.guide.shortHint'
                : health.resetBeforeEmpty
                  ? 'admin.usage.guide.resetHint'
                  : health.daily != null
                    ? 'admin.usage.guide.forecastHint'
                    : health.refillAt != null
                      ? 'admin.usage.guide.allowanceHint'
                      : 'admin.usage.guide.unavailableHint'
            )
            return health.shortNeeded == null
              ? text
              : text
                  .replace('{needed}', () => el.scope.formatCredits(health.shortNeeded))
                  .replace('{available}', () => el.scope.formatCredits(health.total))
          }
        }
      },
      Button: {
        theme: 'primary',
        text: (el) => el.call('polyglot', 'admin.usage.nav.topup'),
        onClick: (event, el, s) => s.update({ usageView: 'topup' })
      }
    },
    Allocation: {
      hide: (el, s) => !el.scope.available(s.root) || el.scope.health(s.root).kind === 'allocation',
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 'A',
      childExtends: 'AdminUsageMetric',
      childrenAs: 'state',
      children: (el, s) => {
        const balance = el.scope.balance(s.root)
        return [
          {
            label: el.call('polyglot', 'admin.usage.balance.fromGrant'),
            value: el.scope.formatCredits(balance.grantRemaining)
          },
          {
            label: el.call('polyglot', 'admin.usage.balance.fromTopups'),
            value: el.scope.formatCredits(balance.topupRemaining)
          }
        ]
      }
    }
  },
  AdminCard_1: {
    padding: 'A',
    flow: 'y',
    gap: 'Z1',
    minWidth: '0',
    Label: {
      text: (el) => el.call('polyglot', 'admin.usage.tiers.current'),
      fontSize: 'Z',
      color: 'inactive'
    },
    Name: {
      fontSize: 'D',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => el.scope.plan(s.root)?.displayName || el.scope.balance(s.root).tier || '—'
    },
    Price: {
      color: 'inactive',
      fontSize: 'Z',
      text: (el, s) => {
        const plan = el.scope.plan(s.root)
        if (!plan) return el.call('polyglot', 'admin.usage.summary.priceUnavailable')
        if (plan.priceMonthlyUsd == null)
          return el.call('polyglot', 'admin.usage.summary.customPrice')
        if (plan.priceMonthlyUsd === 0) return el.call('polyglot', 'admin.usage.summary.free')
        const amount = Number(plan.priceMonthlyUsd).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2
        })
        const price = el
          .call(
            'polyglot',
            plan.priceIsFloor ? 'admin.usage.tier.priceFrom' : 'admin.usage.tier.priceMonthly'
          )
          .replace('{price}', amount)
        return el.call('polyglot', 'admin.usage.summary.catalogPrice').replace('{price}', price)
      }
    },
    Reset: {
      padding: 'Z',
      background: 'widgetPlate',
      round: 'radiusControl',
      color: 'caption',
      fontSize: 'Y2',
      text: (el, s) => {
        const balance = el.scope.balance(s.root)
        if (balance.scope === 'organization')
          return el.call('polyglot', 'admin.usage.balance.poolOrgHint')
        if (!balance.nextResetAt) return el.call('polyglot', 'admin.usage.summary.noReset')
        const date = new Date(balance.nextResetAt)
        if (Number.isNaN(date.getTime())) return el.call('polyglot', 'admin.usage.summary.noReset')
        return el
          .call('polyglot', 'admin.usage.balance.nextReset')
          .replace(
            '{date}',
            el.call('localizedDate', date, { month: 'short', day: 'numeric', year: 'numeric' })
          )
      }
    },
    SavedGrant: {
      hide: (el, s) => !el.scope.balance(s.root).grantDivergesFromTierDefault,
      color: 'inactive',
      fontSize: 'Y2',
      text: (el) => el.call('polyglot', 'admin.usage.summary.saved')
    },
    TopupPrice: {
      flow: 'y',
      gap: 'Y',
      padding: 'A',
      round: 'radiusControl',
      background: 'title',
      hide: (el, s) => {
        const rates = s.root._usageRates?.data || s.root._usageRates || {}
        return !(rates.centsPerCredit > 0 && rates.topupMinCents > 0)
      },
      Label: {
        fontSize: 'Z',
        fontWeight: '500',
        color: 'title-reversed',
        text: '{{ admin.usage.nav.topup | polyglot }}'
      },
      Price: {
        fontSize: 'B',
        fontWeight: '700',
        color: 'title-reversed',
        text: (el, s) => {
          const rates = s.root._usageRates?.data || s.root._usageRates || {}
          const price = (Number(rates.topupMinCents) / 100).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD'
          })
          return el
            .call('polyglot', 'admin.usage.summary.topupPrice')
            .replace('{price}', price)
            .replace(
              '{credits}',
              el.scope.formatCredits(Math.floor(rates.topupMinCents / rates.centsPerCredit))
            )
        }
      },
      Note: {
        fontSize: 'Y2',
        color: 'title-reversed',
        text: '{{ admin.usage.summary.topupNote | polyglot }}'
      }
    },
    Actions: {
      marginTop: 'auto',
      paddingTop: 'A',
      flow: 'x',
      flexWrap: 'wrap',
      gap: 'Y',
      Button: {
        theme: 'primary',
        text: (el) => el.call('polyglot', 'admin.usage.summary.compare'),
        onClick: (event, el, s) => s.update({ usageView: 'plans' })
      },
      Button_2: {
        theme: 'transparent',
        text: (el) => el.call('polyglot', 'admin.usage.nav.topup'),
        onClick: (event, el, s) => s.update({ usageView: 'topup' })
      }
    }
  }
}

export const AdminUsageMetric = {
  flow: 'y',
  gap: 'Y',
  minWidth: '0',
  Label: { fontSize: 'Y2', color: 'inactive', text: (el, s) => el.call('polyglot', s.label || '') },
  Value: {
    fontSize: 'B',
    fontWeight: '700',
    color: 'title',
    fontVariantNumeric: 'tabular-nums',
    overflowWrap: 'anywhere',
    text: (el, s) => s.value
  }
}

export const AdminUsageViewButton = {
  extends: 'Button',
  theme: 'transparent',
  minHeight: 'controlHCompact',
  round: 'radiusPill',
  fontSize: 'Z',
  whiteSpace: 'nowrap',
  flexShrink: '0',
  'aria-pressed': (el, s) => s.usageView === el.view,
  background: (el, s) => (s.usageView === el.view ? 'selected' : 'transparent'),
  color: (el, s) => (s.usageView === el.view ? 'title' : 'inactive'),
  fontWeight: (el, s) => (s.usageView === el.view ? '700' : '400'),
  onClick: (event, el, s) => s.update({ usageView: el.view })
}

export const AdminUsageBreakdownRow = {
  flow: 'y',
  gap: 'Y',
  padding: 'Z 0',
  LabelRow: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    Label: { fontSize: 'Z', color: 'title', text: (el, s) => el.call('polyglot', s.label || '') },
    Value: {
      fontSize: 'Z',
      fontVariantNumeric: 'tabular-nums',
      color: 'title',
      text: (el, s) => s.value
    }
  },
  ProgressBar: {
    hide: (el, s) => s.share == null,
    height: 'Y',
    background: 'selected',
    'aria-label': (el, s) => el.call('polyglot', s.label || ''),
    'aria-valuetext': (el, s) => `${s.value} · ${Math.round(s.share * 100)}%`,
    'aria-valuenow': (el, s) => String(Math.round((s.share || 0) * 100)),
    value: (el, s) => s.share || 0,
    Fill: {
      transition: 'none',
      width: (el, s) => `${Math.min(1, Math.max(0, s.share || 0)) * 100}%`
    }
  },
  Hint: { fontSize: 'Y2', color: 'inactive', text: (el, s) => s.hint }
}

export const AdminUsageLegend = {
  flow: 'x',
  align: 'center',
  gap: 'Y',
  Dot: { boxSize: 'Y', round: 'radiusPill', background: (el, s) => s.color, 'aria-hidden': 'true' },
  Label: { fontSize: 'Y2', color: 'caption', text: (el, s) => el.call('polyglot', s.label) },
  Value: { fontSize: 'Z', fontWeight: '700', color: 'title', text: (el, s) => s.value }
}
